import type { ComponentType } from 'react';
import { Layout } from './components/Layout';
import { AuthProvider, useAuth } from './lib/auth';
import { useRoute } from './lib/router';
import { LoginPage } from './features/auth/LoginPage';
import { UnauthorizedPage } from './features/auth/UnauthorizedPage';
import { AdminLoader } from './components/AdminLoader';
import { StepUpDialog } from './features/auth/StepUpDialog';
import { lazyPage } from './lib/lazy-page';

// Each page is its own chunk, loaded on first visit (Suspense + error boundary live in Layout).
const AccountPage = lazyPage(() => import('./features/account/AccountPage'), 'AccountPage');
const AiConsolePage = lazyPage(() => import('./features/ai/AiConsolePage'), 'AiConsolePage');
const AuditPage = lazyPage(() => import('./features/audit/AuditPage'), 'AuditPage');
const PyqImportPage = lazyPage(
  () => import('./features/pyq-import/PyqImportPage'),
  'PyqImportPage',
);
const GenerationPage = lazyPage(() => import('./features/items/GenerationPage'), 'GenerationPage');
const ImportPage = lazyPage(() => import('./features/items/ImportPage'), 'ImportPage');
const QualityPage = lazyPage(() => import('./features/items/QualityPage'), 'QualityPage');
const ReviewPage = lazyPage(() => import('./features/items/ReviewPage'), 'ReviewPage');
const StudioPage = lazyPage(() => import('./features/items/StudioPage'), 'StudioPage');
const TodayPage = lazyPage(() => import('./features/items/TodayPage'), 'TodayPage');
const AskPage = lazyPage(() => import('./features/kb/AskPage'), 'AskPage');
const ChunkRedirect = lazyPage(() => import('./features/kb/ChunkRedirect'), 'ChunkRedirect');
const LibraryPage = lazyPage(() => import('./features/kb/LibraryPage'), 'LibraryPage');
const SourcePage = lazyPage(() => import('./features/kb/SourcePage'), 'SourcePage');
const GrandMockPage = lazyPage(
  () => import('./features/grand-mock/GrandMockPage'),
  'GrandMockPage',
);
const HomePage = lazyPage(() => import('./features/home/HomePage'), 'HomePage');
const EarlyBirdPage = lazyPage(
  () => import('./features/early-bird/EarlyBirdPage'),
  'EarlyBirdPage',
);
const ProductPage = lazyPage(() => import('./features/product/ProductPage'), 'ProductPage');
const ProductsPage = lazyPage(() => import('./features/product/ProductsPage'), 'ProductsPage');
const StudentsPage = lazyPage(() => import('./features/students/StudentsPage'), 'StudentsPage');
const ActivityPage = lazyPage(() => import('./features/activity'), 'ActivityPage');
const FlashcardsPage = lazyPage(
  () => import('./features/flashcards/FlashcardsPage'),
  'FlashcardsPage',
);
const SupportPage = lazyPage(() => import('./features/support/SupportPage'), 'SupportPage');
const UsersPage = lazyPage(() => import('./features/users/UsersPage'), 'UsersPage');

/** `#/kb`, `#/kb/ask`, `#/kb/chunks/<id>`, `#/kb/sources/<id>[/<tab>[/<page>[/<chunkId>]]]`. */
function kbPage(route: string[]) {
  const [, sub, id, tab, page, chunkId] = route;
  if (sub === 'ask') return <AskPage />;
  if (sub === 'chunks' && id) return <ChunkRedirect chunkId={id} />;
  if (sub === 'sources' && id) {
    const n = Number(page);
    return (
      <SourcePage
        sourceId={id}
        tab={tab ?? 'overview'}
        page={Number.isInteger(n) && n > 0 ? n : 1}
        chunkId={chunkId}
      />
    );
  }
  return <LibraryPage />;
}

/** Sections whose page takes no route params. */
const PLAIN_PAGES: Partial<Record<string, ComponentType>> = {
  'early-bird': EarlyBirdPage,
  activity: ActivityPage,
  today: TodayPage,
  review: ReviewPage,
  quality: QualityPage,
  generation: GenerationPage,
  import: ImportPage,
  users: UsersPage,
  audit: AuditPage,
  account: AccountPage,
};

function Routes() {
  const { me, loading, expired, clearExpired } = useAuth();
  const route = useRoute();
  if (loading) return <AdminLoader label="Checking your session…" />;
  if (me?.stage !== 'active')
    return expired ? (
      <UnauthorizedPage onSignIn={clearExpired} onHome={clearExpired} />
    ) : (
      <LoginPage />
    );
  const [section, id, tab] = route;
  const Plain = section ? PLAIN_PAGES[section] : undefined;
  let page;
  if (Plain) page = <Plain />;
  else if (section === 'products' && id)
    page = <ProductPage productId={id} tab={tab ?? 'overview'} />;
  else if (section === 'kb') page = kbPage(route);
  else if (section === 'grand-mock') page = <GrandMockPage route={route} />;
  else if (section === 'flashcards') page = <FlashcardsPage route={route} />;
  else if (section === 'students') page = <StudentsPage route={route} />;
  else if (section === 'support') page = <SupportPage route={route} />;
  else if (section === 'studio')
    page = id === 'job' ? <StudioPage jobId={tab} /> : <StudioPage itemId={id} />;
  else if (section === 'pyq-import') page = <PyqImportPage batchId={id} />;
  else if (section === 'ai') page = <AiConsolePage tab={id ?? 'overview'} />;
  // Home (to-do cards) is the landing page for everyone; the old Today page is under Questions.
  else if (!section || section === 'home') page = <HomePage />;
  else page = <ProductsPage />;
  return (
    <Layout route={route}>
      {page}
      <StepUpDialog />
    </Layout>
  );
}

/**
 * Admin portal: sign-in with 2FA, products, exam packs, config overrides, syllabus, users, audit
 * (M1); knowledge base library, page/chunk viewer, search & ask, AI console (M2); Today, review
 * queue, quality inbox, question studio, generation jobs, imports (M3). Organised by task: Home,
 * Questions, Exams & mocks (incl. the SSC Rankers League), Students (support), Money (early-bird),
 * Settings.
 */
export function App() {
  return (
    <AuthProvider>
      <Routes />
    </AuthProvider>
  );
}
